import React from "react";

import {
  Drawer,
  Box,
  Chip,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IosShareIcon from "@mui/icons-material/IosShare";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import { Link } from "react-router-dom";

import {
  EpisodesIcon,
  UploadEpisodeIcon,
  ProjectsFolderIcon,
  ShareNetworkIcon,
  UsersIcon,
} from "../../icons";
import { SidebarListProps } from "../types";
import { useUserState } from "../../../../store";
import { RoleName } from "../../enums/RoleName";
import { BRAND_COLORS } from "../../../../constants";

interface MobileSidebarProps {
  open: boolean;
  onClose: () => void;
  pageName: string;
}

const PRIMARY = BRAND_COLORS.primary;
const ACTIVE_BG = "rgba(37, 99, 235, 0.08)";
const HOVER_BG = "rgba(0, 0, 0, 0.04)";
const BORDER = "rgba(0, 0, 0, 0.08)";

export default function MobileSidebar({
  open,
  onClose,
  pageName,
}: MobileSidebarProps) {
  const { user } = useUserState();
  const sidebarList: SidebarListProps[] = [
    {
      icon: <EpisodesIcon />,
      name: "Episodes",
      link: "/episodes",
      isSelected: pageName === "Episodes",
      permissions: [RoleName.ADMIN, RoleName.EDITOR, RoleName.VIEWER],
    },
    {
      icon: <UploadEpisodeIcon />,
      name: "Upload Episode",
      link: "/episodes/upload",
      isSelected: pageName === "Upload Episode",
      permissions: [RoleName.ADMIN, RoleName.EDITOR],
    },
    {
      icon: <ProjectsFolderIcon />,
      name: "Projects",
      link: "/projects",
      isSelected: pageName === "Projects",
      permissions: [RoleName.ADMIN, RoleName.EDITOR, RoleName.VIEWER],
    },
    {
      icon: <ShareNetworkIcon />,
      name: "Shared Episodes",
      link: "/shared-episodes",
      disabled: true,
      isSelected: false,
      permissions: [RoleName.ADMIN, RoleName.EDITOR, RoleName.VIEWER],
      subMenu: [
        {
          icon: <IosShareIcon />,
          name: "Shared Episodes",
          link: "/shared-episodes",
          isSelected: pageName === "Shared Episodes",
        },
        {
          icon: <CoPresentIcon />,
          name: "Episodes Shared With Me",
          link: "/episodes-shared-with-me",
          isSelected: pageName === "Episodes Shared With Me",
        },
      ],
    },
  ];

  const sidebarBottomList: SidebarListProps[] = [
    {
      icon: <UsersIcon />,
      name: "Users",
      link: "/users",
      isSelected: pageName === "Users",
      permissions: [RoleName.ADMIN, RoleName.EDITOR],
    },
  ];

  const renderNavItem = (item: SidebarListProps, compact = false) => (
    <ListItemButton
      {...(item.disabled ? {} : { component: Link, to: item.link })}
      key={item.name}
      disabled={item.disabled}
      aria-current={item.isSelected ? "page" : undefined}
      sx={{
        py: compact ? 1 : 1.25,
        px: 1.5,
        mb: 0.5,
        borderRadius: "8px",
        backgroundColor: item.isSelected ? ACTIVE_BG : "transparent",
        color: item.isSelected ? PRIMARY : "text.secondary",
        "&:hover": {
          backgroundColor: item.isSelected ? ACTIVE_BG : HOVER_BG,
          color: item.isSelected ? PRIMARY : "text.primary",
        },
        "&.Mui-disabled": { opacity: 0.45 },
        transition: "all 0.15s ease",
      }}
      onClick={onClose}
    >
      <ListItemIcon
        sx={{
          minWidth: "auto",
          mr: 1.5,
          color: "inherit",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "& .MuiSvgIcon-root": {
            fontSize: compact ? 18 : 20,
            color: "inherit",
          },
        }}
      >
        {item.icon}
      </ListItemIcon>
      <ListItemText
        primaryTypographyProps={{
          fontSize: compact ? 13 : 14,
          fontWeight: item.isSelected ? 600 : 400,
          letterSpacing: 0.1,
          color: "inherit",
        }}
        primary={item.name}
      />
      {item.disabled && (
        <Chip
          label="Soon"
          size="small"
          sx={{
            height: 18,
            ml: 1,
            fontSize: "9px",
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: PRIMARY,
            backgroundColor: `${PRIMARY}14`,
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
      )}
    </ListItemButton>
  );

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        "& .MuiDrawer-paper": {
          width: 260,
          backgroundColor: "#FFFFFF",
          border: "none",
          borderRight: `1px solid ${BORDER}`,
          boxShadow: "4px 0 24px rgba(0,0,0,0.08)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          minHeight: 72,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <img
            src="/logo.png"
            alt="Podcast Assistant Logo"
            style={{ width: 36, height: 36, display: "block" }}
          />
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.9rem",
              color: "text.primary",
              letterSpacing: "-0.01em",
            }}
          >
            Podcast Assistant
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: "text.secondary",
            width: 28,
            height: 28,
            borderRadius: "6px",
            border: `1px solid ${BORDER}`,
            "&:hover": { backgroundColor: HOVER_BG, color: PRIMARY },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* MENU label */}
      <Box sx={{ px: 2.5, pt: 2.5, pb: 0.5 }}>
        <Typography
          sx={{
            fontSize: "10px",
            fontWeight: 700,
            color: "text.secondary",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          Menu
        </Typography>
      </Box>

      {/* Nav items */}
      <Box sx={{ flex: 1, overflow: "auto", px: 1.5, pt: 0.5 }}>
        <List sx={{ py: 0 }}>
          {sidebarList
            .filter((item) =>
              item.permissions?.some((permission) =>
                user?.roles?.includes(permission),
              ),
            )
            .map((item) => (
              <Box key={item.name}>
                {renderNavItem(item)}
                {item.subMenu &&
                  item.subMenu.some((s: SidebarListProps) => s.isSelected) && (
                    <List sx={{ pl: 2, py: 0 }}>
                      {item.subMenu.map((subItem: SidebarListProps) =>
                        renderNavItem(subItem, true),
                      )}
                    </List>
                  )}
              </Box>
            ))}
        </List>
      </Box>

      {/* Bottom items */}
      <Box
        sx={{
          borderTop: `1px solid ${BORDER}`,
          px: 1.5,
          py: 1,
        }}
      >
        <List sx={{ py: 0 }}>
          {sidebarBottomList
            .filter((item) =>
              item.permissions?.some((permission) =>
                user?.roles?.includes(permission),
              ),
            )
            .map((item) => renderNavItem(item))}
        </List>
      </Box>
    </Drawer>
  );
}
